import isFunction from 'lodash/isFunction'
import noop from 'lodash/noop'
import { Message, MessageType } from './message'

export class Stream<SV = any, SE = any, SC = any, DV = SV, DE = SE, DC = SC> implements FullSubscription<SV, SE, SC> {
    closed = false
    connected = false

    protected subscriptions = new Set<Subscription<DV, DE, DC>>()

    connect() {
        this.connected = true
    }
    disconnect() {
        this.connected = false
    }

    value(value: SV) {
        this._value(value as any)
    }
    error(error?: SE) {
        this._error(error as any)
    }
    complete(complete?: SC) {
        this._complete(complete as any)
    }

    subscribeOnce(value: (value: DV) => void): () => void {
        let done = false
        const unSub = this.subscribe((...args) => {
            value(...args)
            done = true
            unSub?.()
        })

        if (done) {
            unSub()
        }

        return unSub
    }

    subscribe(value: (value: DV) => void): () => void
    subscribe(subscription: Subscription<DV, DE, DC>): () => void
    subscribe(valueOrSubscription?: Subscription<DV, DE, DC> | ((value: DV) => void)) {
        const isSubscription = !isFunction(valueOrSubscription)

        const subscription = isSubscription
            ? (valueOrSubscription as Subscription<DV, DE, DC>)
            : { value: valueOrSubscription as (value: DV) => void }

        if (this.subscriptions.has(subscription)) {
            throw new Error(`already subscribed`)
        }
        if (this.closed) return noop

        this.subscriptions.add(subscription)

        if (!this.connected && this.subscriptions.size === 1) this.connect()

        return () => this.unsubscribe(subscription)
    }

    unsubscribe(value: (value: DV) => void): void
    unsubscribe(subscription: Subscription<DV, DE, DC>): void
    unsubscribe(valueOrSubscription: Subscription<DV, DE, DC> | ((value: DV) => void)): void {
        if (isFunction(valueOrSubscription)) {
            const onValue = valueOrSubscription
            const subscription = [...this.subscriptions.values()].find((x) => x.value === onValue)
            if (!subscription) {
                if (process.env.NODE_ENV === 'development') {
                    // throw new Error(`subscription not found`)
                    console.warn(`subscription not found`)
                } else {
                    return
                }
            }

            if (subscription) {
                this.subscriptions.delete(subscription)
            }
        } else {
            const subscription = valueOrSubscription as Subscription<DV, DE, DC>
            if (!this.subscriptions.has(subscription)) {
                if (process.env.NODE_ENV === 'development') {
                    // throw new Error(`subscription not found`)
                    console.warn(`subscription not found`)
                } else {
                    return
                }
            }

            this.subscriptions.delete(subscription)
        }

        if (this.connected && this.subscriptions.size === 0) this.disconnect()
    }

    publish(message: Message<SV, SE, SC>) {
        switch (message.type) {
            case MessageType.Value:
                this.value(message.value)
                break
            case MessageType.Error:
                this.error(message.error)
                break
            case MessageType.Complete:
                this.complete(message.complete)
                break
        }
    }

    internalPublish(message: Message<DV, DE, DC>) {
        switch (message.type) {
            case MessageType.Value:
                this._value(message.value)
                break
            case MessageType.Error:
                this._error(message.error)
                break
            case MessageType.Complete:
                this._complete(message.complete)
                break
        }
    }

    protected close() {
        if (this.closed) return
        this.closed = true

        if (this.connected) this.disconnect()

        this.subscriptions.clear()
    }

    protected _value(value: DV) {
        if (this.closed) return

        for (const subscription of this.subscriptions) {
            if (subscription.value) subscription.value(value)
        }
    }

    protected _error(error: DE) {
        if (this.closed) return

        for (const subscription of this.subscriptions) {
            if (subscription.error) subscription.error(error)
        }
    }

    protected _complete(complete: DC) {
        if (this.closed) return

        if (this.connected) {
            for (const subscription of this.subscriptions) {
                if (subscription.complete) subscription.complete(complete)
            }
        }

        this.close()
    }
}

export type MirrorStream<V = any, E = any, C = any> = Stream<V, E, C>
export type AnySourceStream<V = any, E = any, C = any> = Stream<any, any, any, V, E, C>
export type AnyDrainStream<V = any, E = any, C = any> = Stream<V, E, C, any, any, any>

export interface Subscription<V = any, E = any, C = any> {
    value?(value: V): void
    error?(error: E): void
    complete?(complete: C): void
}

export interface FullSubscription<V = any, E = any, C = any> extends Subscription<V, E, C> {
    value(value: V): void
    error(error: E): void
    complete(complete: C): void
}

export type AnySubscription = Subscription<any, any, any>

export type UnpackedStream<OriginalValue> = OriginalValue extends Stream<any, any, any, infer StreamValue>
    ? StreamValue
    : OriginalValue
export type UnpackedStreamOrPromise<OriginalValue> = OriginalValue extends Stream<any, any, any, infer StreamValue>
    ? StreamValue
    : OriginalValue extends Promise<infer PromiseValue>
    ? PromiseValue
    : OriginalValue
